import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import i18n from "src/config/i18n";
import SuspenseLoading from "src/Components/UI/SuspenseLoading/SuspenseLoading";
import { getAccessToken, isAccessTokenExpired } from "src/Funcs/auth/authStore";
import { ensureSession, buildLoginUrl } from "src/Funcs/auth/session";
import {
  probeBoardAccess,
  setAnonymousPublicBoard,
} from "src/Funcs/auth/boardAccess";

function GateLoading() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--white)]">
      <SuspenseLoading />
    </div>
  );
}

/**
 * Route-level auth middleware for the SPA. Mount it as a layout route
 * (`<Route element={<RequireAuth />}>`) wrapping every protected route.
 *
 * Semantics (runs before children render — the lazy page chunk is never
 * fetched when access is denied):
 *  - fresh access token             -> render <Outlet/> immediately
 *  - expired/missing access token   -> single-flight refresh, then decide
 *  - no refresh token / refresh 401 -> <Navigate to="/?redirect=..."> so the
 *    user returns here after logging in
 */
export function RequireAuth() {
  const location = useLocation();
  const [status, setStatus] = useState(() =>
    getAccessToken() && !isAccessTokenExpired() ? "allow" : "checking",
  );

  useEffect(() => {
    // Re-evaluate on every navigation: the access-token cookie (15 min) may
    // expire while the SPA sits open, and a mid-session logout/expiry must
    // bounce the next route change back to the gate.
    if (getAccessToken() && !isAccessTokenExpired()) return;
    if (status === "checking") return;
    setStatus("checking");
  }, [location.pathname]);

  useEffect(() => {
    if (status !== "checking") return;
    let alive = true;
    ensureSession().then((ok) => {
      if (alive) setStatus(ok ? "allow" : "deny");
    });
    return () => {
      alive = false;
    };
  }, [status]);

  if (status === "checking") return <GateLoading />;
  if (status === "deny") {
    return (
      <Navigate
        to={buildLoginUrl(location.pathname, location.search)}
        replace
      />
    );
  }
  return <Outlet />;
}

/**
 * Gate for /Board?ID=... — public boards are viewable without login, private
 * boards need a session + membership.
 *
 *  - public board                     -> render (anonymous allowed)
 *  - private + no session             -> /?redirect=/Board?ID=...
 *  - private + logged-in non-member   -> /Dashboard
 *  - missing/unknown board            -> /Dashboard (logged-in) or login
 */
export function BoardGate({ children }) {
  const location = useLocation();
  const boardId = new URLSearchParams(location.search).get("ID");
  const [state, setState] = useState("checking");

  useEffect(() => {
    let alive = true;
    setAnonymousPublicBoard(false);
    // Re-gate on every board switch so a denied board never keeps the
    // previous board mounted.
    setState("checking");

    (async () => {
      if (!boardId) {
        if (alive) setState("dashboard");
        return;
      }

      const authed = await ensureSession();
      const verdict = await probeBoardAccess(boardId);
      if (!alive) return;

      if (verdict.outcome === "allow") {
        setAnonymousPublicBoard(!authed && verdict.publicBoard);
        setState("allow");
      } else if (authed) {
        if (verdict.outcome === "deny") {
          toast.error(
            i18n.t("NoAccessToBoard", "You don't have access to this board"),
          );
        }
        setState("dashboard");
      } else {
        setState("login");
      }
    })();

    return () => {
      alive = false;
      setAnonymousPublicBoard(false);
    };
  }, [boardId]);

  if (state === "checking") return <GateLoading />;
  if (state === "dashboard") return <Navigate to="/Dashboard" replace />;
  if (state === "login") {
    return (
      <Navigate
        to={buildLoginUrl(location.pathname, location.search)}
        replace
      />
    );
  }
  return children;
}

export default RequireAuth;
